import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, ArrowRight, Check, RotateCcw, X } from 'lucide-react'
import { GUIDED_TOUR_STEPS, guidedTourStepComplete, type GuidedTourSession } from '../guided-tour'
import type { DeckProject, WorkspaceTab } from '../types'

interface GuidedTourProps {
  session: GuidedTourSession
  project: DeckProject
  activeTab: WorkspaceTab
  onStepChange: (stepIndex: number) => void
  onExit: () => void
  onRestart: () => void
  onComplete: () => void
  onRequestTab: (tab: WorkspaceTab) => void
}

interface TargetRect {
  left: number
  top: number
  width: number
  height: number
}

export function GuidedTour({ session, project, activeTab, onStepChange, onExit, onRestart, onComplete, onRequestTab }: GuidedTourProps) {
  const step = GUIDED_TOUR_STEPS[session.stepIndex] ?? GUIDED_TOUR_STEPS[0]
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null)
  const [confirmRestart, setConfirmRestart] = useState(false)
  const complete = guidedTourStepComplete(step.id, project, activeTab)
  const lastStep = session.stepIndex === GUIDED_TOUR_STEPS.length - 1

  useEffect(() => {
    if (step.tab && activeTab !== step.tab) onRequestTab(step.tab)
  }, [activeTab, onRequestTab, step.tab])

  useEffect(() => {
    let observedTarget: HTMLElement | null = null
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(refresh)
    function refresh() {
      const target = document.querySelector<HTMLElement>(step.target)
      if (target !== observedTarget) {
        resizeObserver?.disconnect()
        observedTarget = target
        if (target) resizeObserver?.observe(target)
      }
      if (!target) return setTargetRect(null)
      const rect = target.getBoundingClientRect()
      const next = { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
      setTargetRect((current) => current
        && current.left === next.left
        && current.top === next.top
        && current.width === next.width
        && current.height === next.height
        ? current
        : next)
    }
    const frame = window.requestAnimationFrame(refresh)
    const mutationObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver(refresh)
    mutationObserver?.observe(document.body, { childList: true, subtree: true })
    window.addEventListener('resize', refresh)
    window.addEventListener('scroll', refresh, true)
    return () => {
      window.cancelAnimationFrame(frame)
      mutationObserver?.disconnect()
      resizeObserver?.disconnect()
      window.removeEventListener('resize', refresh)
      window.removeEventListener('scroll', refresh, true)
    }
  }, [activeTab, step.target])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (confirmRestart) setConfirmRestart(false)
      else onExit()
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [confirmRestart, onExit])

  const cardSide = useMemo(() => {
    if (!targetRect) return 'right'
    return targetRect.left + targetRect.width / 2 > window.innerWidth / 2 ? 'left' : 'right'
  }, [targetRect])

  const advance = () => {
    if (!complete) return
    if (lastStep) onComplete()
    else onStepChange(session.stepIndex + 1)
  }

  return (
    <div className="guided-tour-layer" aria-live="polite">
      {targetRect && (
        <div
          className="guided-tour-spotlight"
          style={{ left: targetRect.left - 6, top: targetRect.top - 6, width: targetRect.width + 12, height: targetRect.height + 12 }}
        />
      )}
      <section className={`guided-tour-card ${cardSide}`} aria-label="Guided tour">
        {confirmRestart ? (
          <>
            <span className="tour-step-label">Restart tutorial</span>
            <h2>Start this practice deck over?</h2>
            <p>The tutorial project will return to its blank starting point. Your other projects will not change.</p>
            <div className="tour-actions split">
              <button className="button ghost" onClick={() => setConfirmRestart(false)}>Cancel</button>
              <button className="button danger" onClick={onRestart}><RotateCcw size={15} /> Restart</button>
            </div>
          </>
        ) : (
          <>
            <div className="tour-card-heading">
              <span className="tour-step-label">Step {session.stepIndex + 1} of {GUIDED_TOUR_STEPS.length}</span>
              <div>
                <button aria-label="Restart guided tour" title="Restart guided tour" onClick={() => setConfirmRestart(true)}><RotateCcw size={15} /></button>
                <button aria-label="Exit guided tour" title="Exit guided tour" onClick={onExit}><X size={16} /></button>
              </div>
            </div>
            <div className="tour-progress" aria-hidden="true"><span style={{ width: `${((session.stepIndex + 1) / GUIDED_TOUR_STEPS.length) * 100}%` }} /></div>
            <h2>{step.title}</h2>
            <p>{step.instruction}</p>
            {!targetRect && <span className="tour-target-wait">Preparing the highlighted control…</span>}
            {!complete && <span className="tour-action-status">Use the highlighted control to continue.</span>}
            {complete && !step.manual && <span className="tour-action-status complete"><Check size={14} /> Step complete</span>}
            <div className="tour-actions">
              <button className="button ghost" disabled={session.stepIndex === 0} onClick={() => onStepChange(session.stepIndex - 1)}><ArrowLeft size={15} /> Back</button>
              <button className="button primary" disabled={!complete} onClick={advance}>{step.continueLabel ?? 'Next'} <ArrowRight size={15} /></button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}
